import { ProgressBar } from "./ProgressBar";
import { StepSerie } from "./steps/StepSerie";
import { StepCouleur } from "./steps/StepCouleur";
import { StepAile } from "./steps/StepAile";
import { StepBatterie } from "./steps/StepBatterie";
import { StepChargeur } from "./steps/StepChargeur";
import { StepGeneric } from "./steps/StepGeneric";
import { SummaryCard } from "./SummaryCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;
const fp = p => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p || 0);

export function Configurator({ config, brand, currentStep, selection, pricing, sku, onStepChange, onSelectionChange, onNext, onPrev, isComplete }) {
  const steps = config.steps || [];
  const totalSteps = steps.length;
  const isAerofoils = brand === "aerofoils";
  const ci = config.category_images || {};
  const pi = config.product_images || {};

  // Get the selected board_type object for dynamic sub-steps
  const selectedBoardType = (config.board_types || []).find(b => b.id === selection.board_type);

  const getDynamicOptions = (stepId) => {
    if (!selectedBoardType) return [];
    if (stepId === "board_model") return selectedBoardType.models || [];
    if (stepId === "board_color") return selectedBoardType.colors || [];
    return [];
  };

  // Check if a dynamic step should be auto-skipped (only 1 option)
  const shouldAutoSkip = (stepId) => {
    if (stepId !== "board_model" && stepId !== "board_color") return false;
    const opts = getDynamicOptions(stepId);
    return opts.length <= 1;
  };

  // Auto-select single-option dynamic steps
  const handleBoardTypeSelect = (typeId) => {
    onSelectionChange("board_type", typeId);
    // Clear dependent selections
    onSelectionChange("board_model", null);
    onSelectionChange("board_color", null);
    const bt = (config.board_types || []).find(b => b.id === typeId);
    if (bt) {
      // Auto-select model if only one
      if (bt.models && bt.models.length === 1) {
        onSelectionChange("board_model", bt.models[0].id);
      }
      // Auto-select color if only one
      if (bt.colors && bt.colors.length === 1) {
        onSelectionChange("board_color", bt.colors[0].id);
      }
    }
    onNext();
  };

  const handleDynamicSelect = (stepId, value) => {
    onSelectionChange(stepId, value);
    if (stepId === "board_model") {
      // Clear color when model changes
      const bt = selectedBoardType;
      if (bt && bt.colors && bt.colors.length === 1) {
        onSelectionChange("board_color", bt.colors[0].id);
      } else {
        onSelectionChange("board_color", null);
      }
    }
    if (currentStep < totalSteps - 1) onNext();
  };

  // Navigate past auto-skippable steps
  const smartNext = () => {
    let next = currentStep + 1;
    while (next < totalSteps && shouldAutoSkip(steps[next]?.id) && selection[steps[next]?.id]) {
      next++;
    }
    onStepChange(Math.min(next, totalSteps - 1));
  };

  const smartPrev = () => {
    let prev = currentStep - 1;
    while (prev >= 0 && shouldAutoSkip(steps[prev]?.id)) {
      prev--;
    }
    onStepChange(Math.max(prev, 0));
  };

  const renderStep = () => {
    const stepDef = steps[currentStep];
    if (!stepDef) return null;

    if (isAerofoils) {
      switch (stepDef.id) {
        case "serie":
          return <StepSerie series={config.series} selected={selection.serie} onSelect={v => { onSelectionChange("serie", v); onNext(); }} categoryImages={ci} />;
        case "couleur":
          return <StepCouleur colors={config.colors} series={config.series} selectedSerie={selection.serie} selected={selection.couleur} onSelect={v => { onSelectionChange("couleur", v); onNext(); }} productImages={pi} />;
        case "aile":
          return <StepAile wings={config.wings} series={config.series} selectedSerie={selection.serie} selected={selection.aile} onSelect={v => { onSelectionChange("aile", v); onNext(); }} categoryImages={ci} />;
        case "batterie":
          return <StepBatterie batteries={config.batteries} selected={selection.batterie} onSelect={v => { onSelectionChange("batterie", v); onNext(); }} categoryImages={ci} />;
        case "chargeur":
          return <StepChargeur chargers={config.chargers} selectedSerie={selection.serie} selectedBatterie={selection.batterie} selected={selection.chargeur} onSelect={v => onSelectionChange("chargeur", v)} categoryImages={ci} />;
        default:
          return null;
      }
    }

    // Board type selection (shows board categories)
    if (stepDef.id === "board_type") {
      return (
        <StepGeneric
          options={config.board_types || []}
          selected={selection.board_type}
          onSelect={handleBoardTypeSelect}
          stepId="board_type"
          config={config}
          selection={selection}
          categoryImages={ci}
        />
      );
    }

    // Board model selection (Icon/Pro - dynamic based on board_type)
    if (stepDef.id === "board_model") {
      const models = getDynamicOptions("board_model");
      return (
        <StepGeneric
          options={models}
          selected={selection.board_model}
          onSelect={v => handleDynamicSelect("board_model", v)}
          stepId="board_model"
          config={config}
          selection={selection}
          categoryImages={ci}
        />
      );
    }

    // Board color selection (dynamic based on board_type)
    if (stepDef.id === "board_color") {
      const colors = getDynamicOptions("board_color");
      const btId = selection.board_type;
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="step-board_color">
          {colors.map(c => {
            const colorImgKey = `${btId}_${c.id}`;
            const colorImgUrl = ci?.board_colors?.[colorImgKey];
            const boardImgUrl = ci?.board_types?.[btId];
            const imgSrc = colorImgUrl ? `${B}${colorImgUrl}` : (boardImgUrl ? `${B}${boardImgUrl}` : null);
            return (
              <button
                key={c.id}
                onClick={() => handleDynamicSelect("board_color", c.id)}
                data-testid={`color-${c.id}`}
                className={`group flex flex-col items-center gap-3 p-5 rounded-xl border transition-all ${selection.board_color === c.id ? "border-[#39FF14] bg-[#39FF14]/5 shadow-[0_0_20px_rgba(57,255,20,0.15)]" : "border-[#1A3A1A] bg-[#0B0B0B] hover:border-[#39FF14]/50"}`}
              >
                {imgSrc ? (
                  <div className="w-full h-32 bg-gradient-to-b from-[#111] to-[#0D1F0D] rounded-lg flex items-center justify-center overflow-hidden mb-1">
                    <img src={imgSrc} alt={c.name} className="w-full h-full object-contain p-2" />
                  </div>
                ) : (
                  <div
                    className="w-14 h-14 rounded-full border-2 transition-all"
                    style={{ backgroundColor: c.hex || "#888", borderColor: selection.board_color === c.id ? "#39FF14" : "#2A2A2A" }}
                  />
                )}
                <span className={`text-sm font-medium ${selection.board_color === c.id ? "text-[#39FF14]" : "text-white"}`}>{c.name}</span>
                {c.price_delta > 0 && (
                  <span className="text-xs text-yellow-400 font-semibold">+{fp(c.price_delta)}</span>
                )}
              </button>
            );
          })}
        </div>
      );
    }

    // Standard steps (efoil, batterie, etc.)
    const dataKey = stepDef.data_key || stepDef.id;
    const options = config[dataKey] || [];
    return (
      <StepGeneric
        options={options}
        selected={selection[stepDef.id]}
        onSelect={v => { onSelectionChange(stepDef.id, v); if (currentStep < totalSteps - 1) smartNext(); }}
        stepId={stepDef.id}
        config={config}
        selection={selection}
        categoryImages={ci}
      />
    );
  };

  const canNext = () => {
    const stepDef = steps[currentStep];
    if (!stepDef) return false;
    return !!selection[stepDef.id];
  };

  const brandTitle = config.brand_name || "eFoil";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="configurator">
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
          Configurez votre <span className="text-[#39FF14]">{brandTitle}</span>
        </h2>
        <p className="text-[#888]">{totalSteps} etapes pour votre setup parfait</p>
      </div>
      <ProgressBar steps={steps} currentStep={currentStep} selection={selection} onStepClick={onStepChange} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 order-2 lg:order-1">
          <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">{steps[currentStep]?.icon}</span>
              <div>
                <p className="text-xs text-[#888] uppercase tracking-wider">Etape {currentStep + 1}/{totalSteps}</p>
                <h3 className="text-xl font-bold text-white">{steps[currentStep]?.label}</h3>
              </div>
            </div>
            <div className="min-h-[300px]">{renderStep()}</div>
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1A3A1A]">
              <button
                onClick={smartPrev}
                disabled={currentStep === 0}
                data-testid="configurator-prev-btn"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium ${currentStep === 0 ? "text-[#888] cursor-not-allowed" : "text-white border border-[#1A3A1A] hover:border-[#39FF14] hover:text-[#39FF14]"}`}
              >
                <ChevronLeft className="w-5 h-5" />Retour
              </button>
              {currentStep < totalSteps - 1 && (
                <button
                  onClick={smartNext}
                  disabled={!canNext()}
                  data-testid="configurator-next-btn"
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold ${canNext() ? "bg-[#39FF14] text-black hover:bg-[#2BC410] shadow-neon" : "bg-[#1A3A1A] text-[#888] cursor-not-allowed"}`}
                >
                  Suivant<ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24">
            <SummaryCard config={config} brand={brand} selection={selection} pricing={pricing} sku={sku} isComplete={isComplete} />
          </div>
        </div>
      </div>
    </div>
  );
}
